"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useMemo } from "react";
import { ArrowRight, BookOpen, Calendar, ChevronLeft, ChevronRight, Clock, FileText, RefreshCw } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { useCategoryPosts } from "@/hooks/useCategoryPosts";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
import { decodeHtmlEntities } from "@/lib/utils";

// Category 150 = Product Selection, 20 = Mesh & Filtration Fundamentals
const SELECTION_GUIDE_CATEGORY_IDS = [150, 20];
const copy = {
  eyebrow: {
    en: "Technical Library",
    zh: "技术文献库",
    es: "Biblioteca técnica",
    de: "Technische Bibliothek",
    fr: "Bibliothèque technique",
    pt: "Biblioteca técnica"
  },
  h2: {
    en: "Related engineering articles and filtration guides",
    zh: "相关工程技术文章与过滤指南",
    es: "Artículos técnicos de ingeniería y guías de filtración",
    de: "Technische Fachartikel und Filtrationsleitfäden",
    fr: "Articles d'ingénierie et guides de filtration associés",
    pt: "Artigos técnicos de engenharia e guias de filtração"
  },
  lead: {
    en: "Explore technical references on woven wire cloth, custom filter components, aperture calculations, and material selections.",
    zh: "深入了解编织丝网、定制过滤组件、孔径计算及材料选型的技术文献。",
    es: "Explore referencias técnicas sobre tela metálica tejida, componentes de filtro a medida, cálculos de abertura y selección de materiales.",
    de: "Entdecken Sie Fachberichte zu Drahtgeweben, maßgeschneiderten Filterelementen, Maschenweitenberechnungen und Werkstoffauswahl.",
    fr: "Découvrez des références techniques sur la toile métallique tissée, les composants filtrants sur mesure, le calcul d'ouverture et le choix des matériaux.",
    pt: "Explore referências técnicas sobre telas metálicas tecidas, componentes de filtro sob medida, cálculos de abertura e seleção de materiais."
  },
  viewAllBtn: {
    en: "View all guides",
    zh: "查看全部指南",
    es: "Ver todas las guías",
    de: "Alle Leitfäden ansehen",
    fr: "Voir tous les guides",
    pt: "Ver todos os guias"
  },
  requestCustomBtn: {
    en: "Request custom specs",
    zh: "索取定制规格",
    es: "Solicitar especificaciones a medida",
    de: "Sondermaße anfragen",
    fr: "Demander des spécifications sur mesure",
    pt: "Solicitar especificações sob medida"
  },
  readTime: {
    en: "5 min read",
    zh: "5 分钟阅读",
    es: "Lectura de 5 min",
    de: "5 Min. Lesezeit",
    fr: "5 min de lecture",
    pt: "5 min de leitura"
  },
  defaultExcerpt: {
    en: "Read technical notes on wire mesh configurations, micron ratings, and industrial filter assembly specifications.",
    zh: "阅读有关编织丝网结构、微米级过滤精度及工业滤芯组件规格的技术说明。",
    es: "Consulte notas técnicas sobre configuraciones de malla metálica, micras y especificaciones de conjuntos filtrantes industriales.",
    de: "Lesen Sie technische Hinweise zu Maschenkonfigurationen, Filterfeinheiten und Spezifikationen industrieller Filterbaugruppen.",
    fr: "Consultez des notes techniques sur les configurations de toile métallique, les seuils de filtration et les ensembles filtrants industriels.",
    pt: "Leia notas técnicas sobre configurações de malha de arame, micragens e especificações de conjuntos filtrantes industriais."
  },
  pageLabel: {
    en: "Page",
    zh: "第",
    es: "Página",
    de: "Seite",
    fr: "Page",
    pt: "Página"
  },
  ofLabel: {
    en: "of",
    zh: "页 / 共",
    es: "de",
    de: "von",
    fr: "sur",
    pt: "de"
  },
  pagesSuffix: {
    en: "",
    zh: "页",
    es: "",
    de: "",
    fr: "",
    pt: ""
  },
  retryBtn: {
    en: "Try again",
    zh: "重试",
    es: "Reintentar",
    de: "Erneut versuchen",
    fr: "Réessayer",
    pt: "Tentar novamente"
  },
  emptyTitle: {
    en: "No technical articles found",
    zh: "未找到相关技术文章",
    es: "No se encontraron artículos técnicos",
    de: "Keine Fachartikel gefunden",
    fr: "Aucun article technique trouvé",
    pt: "Nenhum artigo técnico encontrado"
  },
  emptyDesc: {
    en: "Check back shortly for updated filtration guides and component data.",
    zh: "请稍后查看更新的过滤指南与组件数据。",
    es: "Vuelva a consultar pronto para ver guías de filtración actualizadas y datos de componentes.",
    de: "Besuchen Sie uns bald wieder für aktualisierte Filtrationsleitfäden und Komponentendaten.",
    fr: "Revenez bientôt pour consulter les guides de filtration et les données de composants mis à jour.",
    pt: "Volte em breve para conferir os guias de filtração atualizados e os dados de componentes."
  },
  fallbackGuideTag: {
    en: "Technical Guide",
    zh: "技术指南",
    es: "Guía técnica",
    de: "Technischer Leitfaden",
    fr: "Guide technique",
    pt: "Guia técnico"
  },
  imgAlt: {
    en: "Filtration technical article illustration",
    zh: "过滤技术文章配图",
    es: "Ilustración de artículo técnico de filtración",
    de: "Illustration zum technischen Filtrationsartikel",
    fr: "Illustration de l'article technique de filtration",
    pt: "Ilustração do artigo técnico de filtração"
  }
};
function fallbackImage(index: number) {
  const images = ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial_wire_mesh_filters.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_mesh_filter_elements.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/woven_wire_cloth_manufacturing.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/precision_filter_tubes_cylinders.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/stainless_steel_screening_media.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/custom_oem_mesh_components.png"];
  return images[index % images.length];
}
function ArticlesConsumer({
  lang
}: {
  lang: Lang;
}) {
  const {
    posts,
    loading,
    ready,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = useCategoryPosts({
    categoryIds: SELECTION_GUIDE_CATEGORY_IDS,
    pageSize: 6
  });
  if (loading || !ready) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col overflow-hidden rounded-lg border border-border bg-card">
            <div className="h-48 w-full bg-muted" />
            <div className="flex flex-1 flex-col p-6">
              <div className="mb-3 h-4 w-24 rounded-md bg-muted" />
              <div className="mb-2 h-6 w-full rounded-md bg-muted" />
              <div className="mb-4 h-4 w-3/4 rounded-md bg-muted" />
              <div className="mt-auto h-4 w-20 rounded-md bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-border bg-card p-8 text-center">
        <p className="text-base text-muted-foreground">
          {pick(lang, COMMON.loadError)}
        </p>
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
            <RefreshCw className="size-3.5" />
            {pick(lang, copy.retryBtn)}
          </Button>
        </div>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="rounded-lg border border-border bg-card p-10 text-center">
        <FileText className="mx-auto mb-3 size-8 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-card-foreground">
          {pick(lang, copy.emptyTitle)}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {pick(lang, copy.emptyDesc)}
        </p>
      </div>;
  }
  const localeMap: Record<Lang, string> = {
    en: "en-US",
    zh: "zh-CN",
    es: "es-ES",
    de: "de-DE",
    fr: "fr-FR",
    pt: "pt-BR"
  };
  return <div className="flex flex-col gap-10">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const postImage = post.featuredImage || fallbackImage(i);
        const formattedDate = post.date ? new Date(post.date).toLocaleDateString(localeMap[lang] || "en-US", {
          year: "numeric",
          month: lang === "zh" ? "long" : "short",
          day: "numeric"
        }) : null;
        const categoryName = post.categories && post.categories.length > 0 ? decodeHtmlEntities(post.categories[0].name) : pick(lang, copy.fallbackGuideTag);
        const destinationUrl = post.link || `/TechnicalResourcesPage`;
        const decodedTitle = decodeHtmlEntities(post.title);
        return <article key={post.id} data-index={i} className="flex h-full flex-col">
              <Card className="flex h-full flex-col overflow-hidden border border-border bg-card transition-[border-color] duration-200 hover:border-primary/50 gap-0 py-0">
                <div className="mf-card-media aspect-16/10 w-full">
                  <img src={postImage} alt={decodedTitle || pick(lang, copy.imgAlt)} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  <div className="absolute top-3 left-3">
                    <Badge variant="outline" className="border border-border/80 bg-background text-foreground">
                      {categoryName}
                    </Badge>
                  </div>
                </div>

                <CardHeader className="gap-2 p-6 pb-2">
                  <div className="flex items-center gap-3 mf-caption text-muted-foreground">
                    {formattedDate && <span className="inline-flex items-center gap-1">
                        <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                        {formattedDate}
                      </span>}
                    <span className="inline-flex items-center gap-1">
                      <Clock className="size-3.5" />
                      {pick(lang, copy.readTime)}
                    </span>
                  </div>
                  <CardTitle className="line-clamp-2 text-lg font-semibold leading-snug tracking-tight text-card-foreground">
                    <Link to={destinationUrl} className="hover:text-primary transition-colors">
                      {decodedTitle}
                    </Link>
                  </CardTitle>
                </CardHeader>

                <CardContent className="p-6 pt-0 flex-1">
                  {post.excerpt ? <div className="line-clamp-3 text-sm leading-relaxed text-muted-foreground" dangerouslySetInnerHTML={{
                __html: post.excerpt
              }} /> : <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {pick(lang, copy.defaultExcerpt)}
                    </p>}
                </CardContent>

                <Separator className="bg-border/60" />

                <CardFooter className="flex items-center justify-between p-6 pt-4">
                  <Link to={destinationUrl} className="inline-flex items-center gap-1.5 mf-caption font-semibold tracking-wide text-primary transition-colors hover:text-primary/80">
                    {pick(lang, COMMON.readMore)}
                    <ArrowRight className="size-3.5" />
                  </Link>
                  <span className="mf-caption text-muted-foreground font-mono">MutualFilter R&amp;D</span>
                </CardFooter>
              </Card>
            </article>;
      })}
      </div>

      {totalPages > 1 && <div className="flex items-center justify-between border-t border-border pt-6">
          <div className="mf-caption font-medium text-muted-foreground">
            {lang === "zh" ? <span>
                {pick(lang, copy.pageLabel)} {currentPage} {pick(lang, copy.ofLabel)} {totalPages} {pick(lang, copy.pagesSuffix)}
              </span> : <span>
                {pick(lang, copy.pageLabel)} {currentPage} {pick(lang, copy.ofLabel)} {totalPages}
              </span>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="gap-1.5">
              <ChevronLeft className="size-4" />
              {pick(lang, COMMON.previous)}
            </Button>
            <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="gap-1.5">
              {pick(lang, COMMON.next)}
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>}
    </div>;
}
export default function RelatedArticles() {
  const {
    lang
  } = useLanguage();
  const wp_query = useMemo(() => {
    return new WP_Query({
      post_type: "post",
      per_page: 50,
      order: "desc",
      orderby: "date",
      status: ["publish"]
    });
  }, []);
  return <section id="relatedarticles" data-nav="light" className="border-t border-border bg-background mf-section text-foreground">
      <div className="mf-container">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 border-b border-border pb-8 md:flex-row md:items-end">
          <div className="max-w-2xl space-y-2">
            <div className="inline-flex items-center gap-2 mf-caption font-semibold uppercase tracking-wider text-primary">
              <BookOpen className="size-3.5" />
              <span>{pick(lang, copy.eyebrow)}</span>
            </div>
            <h2 className="mf-h2 text-foreground">
              {pick(lang, copy.h2)}
            </h2>
            <p className="mf-lead text-muted-foreground">
              {pick(lang, copy.lead)}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/TechnicalResourcesPage">
                {pick(lang, copy.viewAllBtn)}
              </Link>
            </Button>
            <Button asChild>
              <Link to="/RequestAQuotePage">
                {pick(lang, copy.requestCustomBtn)}
              </Link>
            </Button>
          </div>
        </div>

        <PostsProvider key="filter-components-articles" wp_query={wp_query} className="w-full">
          <ArticlesConsumer lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}